import * as React from 'react';
import { Aria, Input, Label } from '../index';

type TextFieldProps = {
  /** A form field doesn't need content. */
  children?: undefined;

  /** The message that describes the form control's error.  */
  error: string;

  /** The unique identifier for the form control. */
  id: string;

  /** Denotes that the form is currently being submitted, marking the form control as readonly. */
  isSubmitting: boolean;

  /** Denotes that the user has interacted with the form control, or the form has been submitted. */
  isTouched: boolean;

  /** The display name for the field. */
  label: string;

  /** The maximum length, if any, supported for the form control's value. */
  maxLength?: number;

  /** The name of the form control. */
  name: string;

  /** The value, if any, to display when the form control has no value. */
  placeholder?: string;

  /** Denotes if the form control requires a value. */
  required: boolean;

  /** The current value of the form control used for max length calculations. */
  value: string;
};

/** A text field is a type of **molecule** that combines the `input` and `label` atoms. */
const TextField = React.forwardRef<HTMLInputElement, TextFieldProps>(
  (
    {
      error,
      id,
      isSubmitting,
      isTouched,
      label,
      maxLength,
      name,
      placeholder,
      required,
      value,
    },
    inputRef
  ) => {
    const errorId = `${id}-error`;
    const hasMaxLength = maxLength !== undefined && maxLength !== null;
    const valueLength =
      value === undefined || value === null ? 0 : value.length;
    const shouldDisplayError = Boolean(error && isTouched);

    return (
      <div>
        <Label
          css={{
            alignItems: 'center',
            display: 'flex',
            justifyContent: 'space-between',
            marginBottom: '8px',
          }}
          htmlFor={id}
        >
          <span>
            {label}
            {hasMaxLength ? (
              <Aria>
                {valueLength} of {maxLength} characters
              </Aria>
            ) : null}
          </span>
          {hasMaxLength || required ? (
            <div>
              {hasMaxLength ? (
                <span
                  aria-hidden
                  css={{
                    color: '#666666',
                    fontWeight: 300,
                  }}
                >
                  {valueLength}/{maxLength}
                </span>
              ) : null}
              {required ? (
                <span
                  aria-hidden
                  css={{
                    color: '#0074CC',
                    fontWeight: 300,
                    marginLeft: '16px',
                  }}
                >
                  Required
                </span>
              ) : null}
            </div>
          ) : null}
        </Label>
        <Input
          aria-describedby={shouldDisplayError ? errorId : undefined}
          aria-invalid={shouldDisplayError}
          css={
            shouldDisplayError
              ? {
                  borderColor: '#A71629',
                }
              : undefined
          }
          id={id}
          maxLength={maxLength}
          name={name}
          placeholder={placeholder}
          readOnly={isSubmitting}
          ref={inputRef}
          required={required}
          type="text"
        />
        {shouldDisplayError ? (
          <span
            aria-live="polite"
            css={{
              color: '#A71629',
              fontFamily: 'Roboto, sans-serif',
              fontSize: '12px',
              lineHeight: '12px',
              marginTop: '4px',
            }}
            id={errorId}
          >
            {error}
          </span>
        ) : null}
      </div>
    );
  }
);

export default TextField;
